import { isItemNode, isSectionNode, ItemTreeNode, SectionTreeNode, TreeNode } from './types';

export type VisibleChecklistItem = {
  node: ItemTreeNode;
  itemIndex: number;
};

/**
 * An expanded section drawn around a flat row: the row paints its slice of
 * the section frame (sides, top padding on the first inner row, bottom edge on
 * the last one), so the flat list looks like nested section accordions.
 */
export type InspectionRowFrame = {
  sectionId: string;
  isFirst: boolean;
  isLast: boolean;
};

export type InspectionVirtualRow =
  | {
      type: 'sectionHeader';
      key: string;
      node: SectionTreeNode;
      frames: InspectionRowFrame[];
    }
  | {
      type: 'sectionContent';
      key: string;
      node: SectionTreeNode;
      frames: InspectionRowFrame[];
    }
  | {
      type: 'checklistItem';
      key: string;
      node: ItemTreeNode;
      itemIndex: number;
      frames: InspectionRowFrame[];
    };

export function getInspectionVirtualRowKey(
  row: InspectionVirtualRow
): string {
  return row.key;
}

export function flattenInspectionChecklistRows(
  tree: TreeNode[],
  expandedIds?: Set<string>
): InspectionVirtualRow[] {
  const rows: InspectionVirtualRow[] = [];
  let itemIndex = 0;
  const openSectionIds: string[] = [];

  const framesForNextRow = (): InspectionRowFrame[] =>
    openSectionIds.map((sectionId) => ({
      sectionId,
      isFirst: false,
      isLast: false,
    }));

  const walk = (nodes: TreeNode[]): void => {
    nodes.forEach((node) => {
      if (isItemNode(node)) {
        rows.push({
          type: 'checklistItem',
          key: `item:${node.id}`,
          node,
          itemIndex,
          frames: framesForNextRow(),
        });
        itemIndex += 1;
        return;
      }

      rows.push({
        type: 'sectionHeader',
        key: `section-header:${node.id}`,
        node,
        frames: framesForNextRow(),
      });

      const sectionExpanded = expandedIds?.has(node.id) ?? true;
      if (!sectionExpanded) {
        return;
      }

      openSectionIds.push(node.id);
      const firstInnerRowIndex = rows.length;
      rows.push({
        type: 'sectionContent',
        key: `section-content:${node.id}`,
        node,
        frames: framesForNextRow(),
      });
      walk(node.children);
      openSectionIds.pop();

      const frameIndex = openSectionIds.length;
      rows[firstInnerRowIndex].frames[frameIndex].isFirst = true;
      rows[rows.length - 1].frames[frameIndex].isLast = true;
    });
  };

  walk(tree);
  return rows;
}

export type ChecklistNodeGroup =
  | { type: 'items'; nodes: ItemTreeNode[] }
  | { type: 'section'; node: SectionTreeNode };

export function groupConsecutiveChecklistNodes(
  nodes: TreeNode[]
): ChecklistNodeGroup[] {
  const groups: ChecklistNodeGroup[] = [];

  nodes.forEach((node) => {
    if (isItemNode(node)) {
      const lastGroup = groups[groups.length - 1];
      if (lastGroup?.type === 'items') {
        lastGroup.nodes.push(node);
        return;
      }

      groups.push({ type: 'items', nodes: [node] });
      return;
    }

    if (isSectionNode(node)) {
      groups.push({ type: 'section', node });
    }
  });

  return groups;
}

export function collectVisibleChecklistItems(
  tree: TreeNode[],
  expandedIds?: Set<string>
): VisibleChecklistItem[] {
  const visibleItems: VisibleChecklistItem[] = [];
  let itemIndex = 0;

  const walk = (nodes: TreeNode[]): void => {
    nodes.forEach((node) => {
      if (isItemNode(node)) {
        visibleItems.push({ node, itemIndex });
        itemIndex += 1;
        return;
      }

      const sectionExpanded = expandedIds?.has(node.id) ?? true;
      if (sectionExpanded) {
        walk(node.children);
      }
    });
  };

  walk(tree);
  return visibleItems;
}
